/**
 * Error Handler Utility
 *
 * IMPORTANT: Never show backend error messages to users.
 * Always return generic, user-friendly messages.
 */

export const getErrorMessage = (operation: string): string => {
    return `An error occurred while ${operation}. Please try again or contact the administrator if the problem persists.`;
};

export const ERROR_MESSAGES = {
    // Generic
    SERVER_ERROR: 'Server error. Please contact the administrator.',
    NETWORK_ERROR: 'Network error. Please check your connection and try again.',
    UNKNOWN_ERROR: 'An unexpected error occurred. Please contact the administrator.',

    // Authentication
    LOGIN_FAILED: 'Login failed. Please check your credentials and try again.',
    LOGOUT_FAILED: 'Logout failed. Please try again.',
    SESSION_EXPIRED: 'Your session has expired. Please login again.',
    UNAUTHORIZED: 'You do not have permission to perform this action.',

    // CRUD Operations
    LOAD_FAILED: 'Failed to load data. Please refresh the page.',
    CREATE_FAILED: 'Failed to create item. Please try again.',
    UPDATE_FAILED: 'Failed to update item. Please try again.',
    DELETE_FAILED: 'Failed to delete item. Please try again.',

    // Specific Resources
    HOUSES_LOAD_FAILED: 'Failed to load houses. Please refresh the page.',
    HOUSES_CREATE_FAILED: 'Failed to create house. Please try again.',
    HOUSES_UPDATE_FAILED: 'Failed to update house. Please try again.',
    HOUSES_DELETE_FAILED: 'Failed to delete house. Please try again.',

    USERS_LOAD_FAILED: 'Failed to load users. Please refresh the page.',
    USERS_CREATE_FAILED: 'Failed to create user. Please try again.',
    USERS_UPDATE_FAILED: 'Failed to update user. Please try again.',
    USERS_DELETE_FAILED: 'Failed to delete user. Please try again.',

    CONTACTS_LOAD_FAILED: 'Failed to load contacts. Please refresh the page.',
    CONTACTS_CREATE_FAILED: 'Failed to create contact. Please try again.',
    CONTACTS_UPDATE_FAILED: 'Failed to update contact. Please try again.',
    CONTACTS_DELETE_FAILED: 'Failed to delete contact. Please try again.',

    CONTACT_REQUESTS_LOAD_FAILED: 'Failed to load contact requests. Please refresh the page.',
    CONTACT_REQUESTS_UPDATE_FAILED: 'Failed to update contact request. Please try again.',
    CONTACT_REQUESTS_DELETE_FAILED: 'Failed to delete contact request. Please try again.',

    RESERVATIONS_LOAD_FAILED: 'Failed to load reservations. Please refresh the page.',
    RESERVATIONS_CREATE_FAILED: 'Failed to create reservation. Please try again.',
    RESERVATIONS_UPDATE_FAILED: 'Failed to update reservation. Please try again.',
    RESERVATIONS_DELETE_FAILED: 'Failed to delete reservation. Please try again.',

    PHOTOS_LOAD_FAILED: 'Failed to load photos. Please refresh the page.',
    PHOTOS_UPLOAD_FAILED: 'Failed to upload photos. Please try again.',
    PHOTOS_DELETE_FAILED: 'Failed to delete photo. Please try again.',

    MESSAGES_LOAD_FAILED: 'Failed to load messages. Please refresh the page.',
    MESSAGES_SEND_FAILED: 'Failed to send message. Please try again.',

    // Validation
    VALIDATION_ERROR: 'Please check your input and try again.',
    REQUIRED_FIELDS: 'Please fill in all required fields.',
};

/**
 * Get a user-friendly error message
 * Generally returns generic messages, but for specific cases (like validation errors)
 * it returns the backend error message
 */
export const handleError = (error: any, defaultMessage: string = ERROR_MESSAGES.SERVER_ERROR): string => {
    // Log the actual error for debugging (only in development)
    if (import.meta.env.DEV) {
        console.error('Error details (dev only):', error);
    }

    // Check if backend returned a specific error message we should show to users
    const backendError = error?.response?.data?.error;

    // Specific backend errors that should be shown to users
    const showableErrors = [
        'cannot delete house: house has active or future reservations',
        'category is required',
        'blog post with this slug already exists',
    ];

    if (backendError && typeof backendError === 'string') {
        // Check if this is an error we should show
        const shouldShow = showableErrors.some(msg => backendError.toLowerCase().includes(msg.toLowerCase()));

        if (shouldShow) {
            // Clean up the error message for better presentation
            if (backendError.includes('cannot delete house')) {
                return 'Cannot delete house with associated reservations. Please remove or cancel all reservations first.';
            }
            return backendError;
        }
    }

    // For all other cases, return the generic message
    return defaultMessage;
};
